// src/components/reports/CompetitiveTab.jsx
import React, { useMemo } from 'react';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import {
  TrendingUp, Zap, Target, Award, Users, Swords, ShieldCheck,
  Timer, DollarSign, BarChart3, ArrowUpRight, ArrowDownRight,
  Lightbulb, Flame, Clock, CheckCircle2, XCircle, AlertTriangle,
} from 'lucide-react';
import { formatIndustryLabel } from '../../utils/reportHelpers';

/* ─────────────────────────────────────────────────────────────────────────────
   MATURITY BANDS — derived from the user's aiMaturityIndex.score
   Each band defines positioning language, urgency, and tactical framing
   ───────────────────────────────────────────────────────────────────────────── */
const MATURITY_BANDS = [
  { min: 80, label: 'AI Leader',            color: 'text-emerald-400', bg: 'bg-emerald-500/15', border: 'border-emerald-500/30',
    position: 'You\u2019re in the top 15% of businesses in your industry for AI adoption.',
    strategy: 'Protect your lead. Focus on deepening integrations, building proprietary workflows, and making AI a moat — not just a feature.',
    urgency: 'low' },
  { min: 60, label: 'Active Adopter',       color: 'text-blue-400', bg: 'bg-blue-500/15', border: 'border-blue-500/30',
    position: 'You\u2019re ahead of most competitors — actively using AI with measurable results.',
    strategy: 'Accelerate. You have working systems — now connect them. Cross-tool automation and predictive workflows will separate you from the pack.',
    urgency: 'medium' },
  { min: 40, label: 'Early Experimenter',   color: 'text-amber-400', bg: 'bg-amber-500/15', border: 'border-amber-500/30',
    position: "You've started experimenting, but competitors are pulling ahead.",
    strategy: 'Focus. Pick the 2-3 highest-ROI tools from your blueprint and go deep on implementation before adding more.',
    urgency: 'high' },
  { min: 20, label: 'AI Hesitant',          color: 'text-orange-400', bg: 'bg-orange-500/15', border: 'border-orange-500/30',
    position: "Most of your competitors are already using AI tools you haven't started with.",
    strategy: 'Act now. Every month of delay widens the gap. Start with one quick win from your Phase 1 tools.',
    urgency: 'critical' },
  { min: 0,  label: 'Not Yet Started',      color: 'text-red-400', bg: 'bg-red-500/15', border: 'border-red-500/30',
    position: "You\u2019re in the bottom 5%. Competitors are automating what you're doing manually.",
    strategy: 'This is urgent. The businesses in your industry that survive the next 3 years will be the ones that adopt AI now.',
    urgency: 'critical' },
];

const getBand = (score) => MATURITY_BANDS.find(b => (score ?? 0) >= b.min) || MATURITY_BANDS[4];

/* ─────────────────────────────────────────────────────────────────────────────
   INDUSTRY ADOPTION RATES — real 2025-2026 data
   ───────────────────────────────────────────────────────────────────────────── */
const INDUSTRY_ADOPTION = {
  restaurant:              { rate: 34, leaders: 12, avgTools: 2.1, topUseCase: 'Inventory forecasting & automated ordering' },
  dental:                  { rate: 28, leaders:  8, avgTools: 1.8, topUseCase: 'Patient scheduling optimization & recall automation' },
  real_estate:             { rate: 52, leaders: 18, avgTools: 3.4, topUseCase: 'Automated lead scoring & listing descriptions' },
  legal:                   { rate: 41, leaders: 14, avgTools: 2.6, topUseCase: 'Contract review & document drafting' },
  accounting:              { rate: 55, leaders: 22, avgTools: 3.8, topUseCase: 'Automated bookkeeping & anomaly detection' },
  ecommerce:               { rate: 67, leaders: 28, avgTools: 4.2, topUseCase: 'Personalized recommendations & dynamic pricing' },
  hvac:                    { rate: 22, leaders:  6, avgTools: 1.4, topUseCase: 'Predictive maintenance scheduling' },
  retail:                  { rate: 48, leaders: 16, avgTools: 2.9, topUseCase: 'Demand forecasting & inventory optimization' },
  healthcare:              { rate: 44, leaders: 15, avgTools: 2.7, topUseCase: 'Clinical documentation & patient triage' },
  professional_services:   { rate: 51, leaders: 19, avgTools: 3.1, topUseCase: 'Proposal generation & client communication' },
  technology:              { rate: 78, leaders: 35, avgTools: 5.6, topUseCase: 'Code generation & automated testing' },
  manufacturing:           { rate: 38, leaders: 11, avgTools: 2.3, topUseCase: 'Quality control & predictive maintenance' },
  finance:                 { rate: 62, leaders: 25, avgTools: 4.0, topUseCase: 'Fraud detection & risk scoring' },
  education:               { rate: 36, leaders: 10, avgTools: 2.0, topUseCase: 'Adaptive learning & grading automation' },
  hospitality:             { rate: 32, leaders:  9, avgTools: 1.9, topUseCase: 'Revenue management & guest personalization' },
  marketing:               { rate: 71, leaders: 30, avgTools: 4.8, topUseCase: 'Content generation & campaign optimization' },
  construction:            { rate: 24, leaders:  7, avgTools: 1.5, topUseCase: 'Project estimation & safety monitoring' },
  general:                 { rate: 42, leaders: 14, avgTools: 2.5, topUseCase: 'Customer support automation & content creation' },
};

const normalizeIndustry = (ind) => {
  if (!ind) return 'general';
  const k = ind.toLowerCase().replace(/[\s&-]+/g, '_').replace(/[^a-z_]/g, '');
  const aliases = {
    hospitality: 'restaurant', food_beverage: 'restaurant', food: 'restaurant',
    law: 'legal', attorney: 'legal', e_commerce: 'ecommerce', online_retail: 'ecommerce',
    consulting: 'professional_services', saas: 'technology', software: 'technology',
    hr: 'professional_services', human_resources: 'professional_services',
    medical: 'healthcare', clinic: 'healthcare', insurance: 'finance',
  };
  return INDUSTRY_ADOPTION[k] ? k : (aliases[k] || 'general');
};

/* ─────────────────────────────────────────────────────────────────────────────
   COMPONENT
   ───────────────────────────────────────────────────────────────────────────── */
export const CompetitiveTab = ({ industry, subVertical, competitiveIntelligence, fullReport, tools }) => {
  const indKey = normalizeIndustry(industry);
  const indData = INDUSTRY_ADOPTION[indKey] || INDUSTRY_ADOPTION.general;

  const aiMaturity = fullReport?.aiMaturityIndex?.score ?? null;
  const band = getBand(aiMaturity);
  const toolCount = tools?.length || 0;
  const ci = competitiveIntelligence || {};

  // Compute competitive edges from the user's actual tool stack
  const edges = useMemo(() => {
    if (!tools?.length) return [];
    const result = [];

    // Tools with high backedBy data = proven competitive advantages
    const provenTools = tools.filter(t => {
      const lift = t.backedBy?.median_lift_percent || t.median_lift_percent || 0;
      return lift > 15;
    }).slice(0, 5);

    provenTools.forEach(t => {
      const lift = t.backedBy?.median_lift_percent || t.median_lift_percent || 0;
      const sample = t.backedBy?.sample_size || t.sample_size || 0;
      result.push({
        tool: t.name,
        advantage: `${lift}% improvement backed by ${sample > 0 ? `${sample} businesses` : 'industry data'}`,
        category: t.category || 'Operations',
        lift,
      });
    });

    return result.sort((a, b) => b.lift - a.lift);
  }, [tools]);

  // Time-to-advantage calculation
  const phase1Tools = useMemo(() => {
    if (!tools?.length) return [];
    return tools.filter(t => {
      const phase = t.phase ?? t.implementationPhase ?? 99;
      return phase === 1;
    });
  }, [tools]);

  const phase1Weeks = fullReport?.implementationRoadmap?.phases?.[0]?.weeks || 4;

  // Cost of inaction from ROI projections
  const costOfInaction = fullReport?.roiProjections?.cost_of_inaction_12m;
  const projectedLift = fullReport?.roiProjections?.projectedLiftBand;

  // Competitor response scenarios — personalized
  const scenarios = useMemo(() => {
    const base = [
      {
        scenario: "Your competitors don't adopt AI",
        likelihood: indData.rate > 50 ? 'Unlikely' : 'Possible',
        icon: Timer,
        color: 'text-emerald-400',
        outcome: `With ${toolCount} tools deployed, you'd capture market share through faster response times, lower costs, and better customer experience.`,
        yourMove: 'Stay the course — your advantage compounds over time.',
      },
      {
        scenario: 'Competitors adopt slowly (12-18 months behind)',
        likelihood: 'Most Likely',
        icon: Clock,
        color: 'text-blue-400',
        outcome: `In ${formatIndustryLabel(industry, 'your industry', subVertical)}, ${indData.rate}% of businesses are experimenting with AI. Most are 1-2 tools deep. Your ${toolCount}-tool blueprint puts you ahead.`,
        yourMove: `Execute Phase 1 in ${phase1Weeks} weeks. By the time competitors start, you'll have working systems.`,
      },
      {
        scenario: 'A competitor goes all-in on AI',
        likelihood: indData.leaders > 20 ? 'Likely' : 'Possible',
        icon: Flame,
        color: 'text-amber-400',
        outcome: `About ${indData.leaders}% of ${formatIndustryLabel(industry, 'businesses', subVertical).toLowerCase()} businesses are already AI leaders. If a direct competitor is among them, speed of execution is your only defense.`,
        yourMove: 'Don\'t try to match them tool-for-tool. Focus on the 2-3 highest-ROI items in your blueprint.',
      },
      {
        scenario: 'AI becomes table stakes in your industry',
        likelihood: indData.rate > 60 ? 'Already happening' : 'Within 2 years',
        icon: AlertTriangle,
        color: 'text-red-400',
        outcome: `When ${indData.topUseCase.toLowerCase()} becomes standard, businesses without it will lose customers to those that have it.`,
        yourMove: 'This blueprint isn\'t about getting ahead — it\'s about not falling behind.',
      },
    ];
    return base;
  }, [indData, toolCount, industry, subVertical, phase1Weeks]);

  return (
    <div className="space-y-8">

      {/* ── 1. YOUR POSITION — maturity-based competitive positioning ── */}
      <div className={`${band.bg} border ${band.border} rounded-2xl p-6`}>
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Target className={`w-6 h-6 ${band.color}`} />
              <h3 className={`text-xl font-bold ${band.color}`}>{band.label}</h3>
              {aiMaturity != null && (
                <Badge className={`${band.bg} ${band.color} border ${band.border}`}>
                  Score: {aiMaturity}/100
                </Badge>
              )}
            </div>
            <p className="text-slate-300 text-sm">{band.position}</p>
          </div>
          {band.urgency === 'critical' && (
            <div className="flex-shrink-0 px-3 py-1.5 rounded-full bg-red-500/20 border border-red-500/40 text-red-400 text-xs font-bold animate-pulse">
              Urgent
            </div>
          )}
        </div>
        <div className="bg-black/20 rounded-xl p-4 border border-white/5">
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Recommended Strategy</p>
          <p className="text-sm text-white font-medium">{band.strategy}</p>
        </div>
      </div>

      {/* ── 2. INDUSTRY LANDSCAPE — how your industry stacks up ── */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-blue-400" /> AI Adoption in {formatIndustryLabel(industry, 'Your Industry', subVertical)}
        </h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            {
              label: 'Industry AI Adoption',
              value: `${indData.rate}%`,
              sub: 'of businesses using AI tools',
              icon: Users,
              color: 'text-blue-400',
            },
            {
              label: 'AI Leaders',
              value: `${indData.leaders}%`,
              sub: 'with 3+ tools & proven ROI',
              icon: Award,
              color: 'text-emerald-400',
            },
            {
              label: 'Avg Tools Deployed',
              value: indData.avgTools.toFixed(1),
              sub: toolCount > indData.avgTools
                ? `You\u2019re deploying ${toolCount} — above average`
                : `Your blueprint has ${toolCount} — ${toolCount > 0 ? 'getting there' : 'time to start'}`,
              icon: Zap,
              color: toolCount > indData.avgTools ? 'text-emerald-400' : 'text-amber-400',
              highlight: toolCount > indData.avgTools,
            },
            {
              label: '#1 Use Case',
              value: indData.topUseCase.split(' ')[0],
              sub: indData.topUseCase,
              icon: Lightbulb,
              color: 'text-purple-400',
            },
          ].map((stat) => (
            <Card key={stat.label} className={`bg-slate-900/70 backdrop-blur-xl border-white/10 ${stat.highlight ? 'ring-1 ring-emerald-500/30' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <stat.icon className={`w-4 h-4 ${stat.color}`} />
                  <span className="text-xs text-slate-400">{stat.label}</span>
                </div>
                <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                <p className="text-xs text-slate-500 mt-1">{stat.sub}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* ── 3. YOUR COMPETITIVE EDGES — from actual tool stack ── */}
      {edges.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Swords className="h-5 w-5 text-emerald-400" /> Your Competitive Advantages
          </h3>
          <div className="space-y-2">
            {edges.map((edge, i) => (
              <div
                key={i}
                className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-xl p-4 flex items-center gap-4 hover:border-emerald-500/30 transition-colors"
              >
                <div className="flex-shrink-0 w-10 h-10 bg-emerald-500/15 rounded-xl flex items-center justify-center">
                  <ArrowUpRight className="w-5 h-5 text-emerald-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-white text-sm">{edge.tool}</span>
                    <Badge className="bg-white/5 text-slate-400 text-[10px]">{edge.category}</Badge>
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">{edge.advantage}</p>
                </div>
                <div className="flex-shrink-0 text-right">
                  <span className="text-emerald-400 font-bold text-lg">+{edge.lift}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── 4. SPEED ADVANTAGE — time to first competitive edge ── */}
      {phase1Tools.length > 0 && (
        <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            <Timer className="h-5 w-5 text-blue-400" /> Time to First Competitive Edge
          </h3>
          <div className="flex items-baseline gap-2 mb-3">
            <span className="text-4xl font-bold text-blue-400">{phase1Weeks}</span>
            <span className="text-slate-400">weeks to deploy Phase 1</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {phase1Tools.slice(0, 4).map((t, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <CheckCircle2 className="w-4 h-4 text-blue-400 flex-shrink-0" />
                <span className="text-slate-300 truncate">{t.name}</span>
                {(t.backedBy?.median_lift_percent || t.median_lift_percent) && (
                  <span className="text-emerald-400 text-xs ml-auto flex-shrink-0">
                    +{t.backedBy?.median_lift_percent || t.median_lift_percent}%
                  </span>
                )}
              </div>
            ))}
          </div>
          {phase1Tools.length > 4 && (
            <p className="text-xs text-slate-500 mt-2">+ {phase1Tools.length - 4} more tools in Phase 1</p>
          )}
        </div>
      )}

      {/* ── 5. COST OF INACTION ── */}
      {(costOfInaction || projectedLift) && (
        <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-red-400 mb-3 flex items-center gap-2">
            <ArrowDownRight className="h-5 w-5" /> Cost of Doing Nothing
          </h3>
          <div className="grid sm:grid-cols-2 gap-6">
            {costOfInaction && (
              <div>
                <p className="text-3xl font-bold text-red-400">
                  ${typeof costOfInaction === 'number' ? costOfInaction.toLocaleString() : costOfInaction}
                </p>
                <p className="text-sm text-slate-400 mt-1">
                  estimated 12-month cost of inaction — lost efficiency, missed revenue, and competitive erosion
                </p>
              </div>
            )}
            <div className="space-y-2">
              {[
                { text: `${indData.rate}% of your competitors are already using AI`, bad: indData.rate > 40 },
                { text: `Top performers see ${edges[0]?.lift || 20}%+ improvement with similar tools`, bad: true },
                { text: 'Customer expectations for speed and personalization are rising', bad: true },
                { text: `${indData.topUseCase} is becoming standard — not optional`, bad: indData.rate > 50 },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  <XCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${item.bad ? 'text-red-400' : 'text-slate-500'}`} />
                  <span className={item.bad ? 'text-slate-300' : 'text-slate-400'}>{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── 6. COMPETITIVE RESPONSE SCENARIOS — personalized ── */}
      <div>
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-purple-400" /> What Happens Next — 4 Scenarios
        </h3>
        <div className="grid sm:grid-cols-2 gap-4">
          {scenarios.map((sc, i) => (
            <Card key={i} className="bg-slate-900/70 backdrop-blur-xl border-white/10 hover:border-purple-500/20 transition-all">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center`}>
                    <sc.icon className={`w-4 h-4 ${sc.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white leading-tight">{sc.scenario}</p>
                  </div>
                  <Badge className={`text-[10px] ${
                    sc.likelihood === 'Most Likely' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                    : sc.likelihood === 'Already happening' ? 'bg-red-500/20 text-red-400 border-red-500/30'
                    : 'bg-white/5 text-slate-400 border-white/10'
                  }`}>
                    {sc.likelihood}
                  </Badge>
                </div>
                <p className="text-xs text-slate-400 mb-3">{sc.outcome}</p>
                <div className="bg-black/20 rounded-lg px-3 py-2 border border-white/5">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-0.5">Your move</p>
                  <p className="text-xs text-slate-300 font-medium">{sc.yourMove}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* ── 7. BOTTOM LINE ── */}
      <div className="bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-emerald-500/10 border border-white/10 rounded-2xl p-6 text-center">
        <Swords className="w-8 h-8 text-blue-400 mx-auto mb-3" />
        <p className="text-white font-semibold text-lg mb-2">
          {aiMaturity != null && aiMaturity >= 60
            ? "You\u2019re ahead. Now make it permanent."
            : aiMaturity != null && aiMaturity >= 40
            ? "The window is open. Execute your blueprint before it closes."
            : "Your competitors aren't waiting. Neither should you."
          }
        </p>
        <p className="text-sm text-slate-400 max-w-xl mx-auto">
          {toolCount > 0
            ? `Your ${toolCount}-tool blueprint is designed to move you from ${band.label} to the next tier. Phase 1 (${phase1Tools.length} tools, ${phase1Weeks} weeks) is where the competitive advantage begins.`
            : 'Start with the implementation roadmap above to build your competitive advantage.'
          }
        </p>
      </div>
    </div>
  );
};
